import React, { useEffect, useState } from 'react';
import cln from 'classnames';
import { useUserInfo } from 'react-render-hooks';
import { ERequestIds } from 'react-memory-optimization/dist/lib/binary/types';
import {
  Selector,
  PortalLayout,
  PopUpHeader,
  SelectedSvg,
} from 'react-gldn-kit';
import {
  PaymentChannelType,
  UserInfo,
} from 'react-memory-optimization/dist/lib/store/user/types';

import AmountInput from 'components/ui/Input/AmountInput';
import InputUserInfo from 'components/ui/Input/InputUserInfo';
import { DEFAULT_INPUT_VALUE } from 'components/ui/Input/Base/constant';
import { DefaultInputType } from 'components/ui/Input/Base/types';
import PhoneInput from 'components/ui/Input/Phone';

import { getUserCurrencyIdSelector } from 'stores/user/personal/selectors';
import { useZustandUserStore } from 'stores/user/personal/store';
import { setFormsInfoAction } from 'stores/money/forms/selectors';
import { useZustandFormsStore } from 'stores/money/forms/store';
import { setPaymentsCurrentStepAction } from 'stores/money/payments/selectors';
import { useZustandPaymentsStore } from 'stores/money/payments/store';

import { useHookFabric } from 'hooks/useHookFabric';

import styles from '../Withdraw.module.css';
import { isFilledForm } from 'utils/validation';
import Input from 'components/ui/Input/Base';
import { Steps } from 'stores/money/payments/types';
import { UpiNetWithdraw } from 'stores/money/forms/types';
import { BANK_NAMES } from './constants';
import ComponentKitBaseButton from 'components/ui/KitComponents/Buttons/BaseButton';
import PortalList from '../../PortalList';

type Props = {
  limit: { min: number; max: number };
  activeChannelId:
    | PaymentChannelType.NetBanking
    | PaymentChannelType.UPI
    | PaymentChannelType.UPI_QR;
};

const UPINet = ({ limit, activeChannelId }: Props) => {
  const [amount, setAmount] = useState<string>('');

  const [phone, setPhone] = useState<string>('');

  const [email, setEmail] = useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const userInfo = useHookFabric<UserInfo>({ fn: useUserInfo });

  const [name, setName] = useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const [sureName, setSurName] =
    useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const currencyId = useZustandUserStore(getUserCurrencyIdSelector);

  const setFormInfo = useZustandFormsStore(setFormsInfoAction);

  const setStep = useZustandPaymentsStore(setPaymentsCurrentStepAction);

  // Notice: 1 === inx + 1 ( id === inx+1)
  const [bankName, setBankName] = useState<string>(BANK_NAMES[1].name);

  const [bankNameId, setBankNameId] = useState<number>(1);

  const [bankCode, setBankCode] =
    useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const [isOpenPortal, setPortalState] = useState<boolean>(false);

  const [branchCode, setBranchCode] = useState<DefaultInputType>(
    activeChannelId === PaymentChannelType.NetBanking
      ? { value: '1', errorText: '' }
      : DEFAULT_INPUT_VALUE
  );

  const [bankAccount, setBankAccount] =
    useState<DefaultInputType>(DEFAULT_INPUT_VALUE);

  const handleChangeInput =
    (inputName: 'bankCode' | 'bankAccount' | 'branchCode') =>
    (value: string) => {
      if (inputName === 'bankAccount') {
        const errorText =
          value.length >= 7 && value.length <= 17 ? '' : 'errors.invalidValue';

        setBankAccount({ value, errorText });

        return;
      }

      if (inputName === 'bankCode') {
        setBankCode({ value, errorText: '' });

        return;
      }

      if (inputName === 'branchCode') {
        setBranchCode({ value, errorText: '' });

        return;
      }
    };

  useEffect(() => {
    const data = [
      amount,
      phone,
      email,
      name,
      sureName,
      bankName,
      bankCode,
      bankAccount,
      branchCode,
    ];

    const isFilled = isFilledForm(data);

    if (isFilled && currencyId) {
      setFormInfo({
        isDeposit: false,
        form: {
          currencyId,
          channelId: activeChannelId,
          name: name.value,
          surname: sureName.value,
          email: email.value,
          phone: phone.slice(3),
          bankCode: bankCode.value,
          branch: branchCode.value,
          bankName,
          accountNumber: bankAccount.value,
          amount,
          requestId: ERequestIds.WithdrawRequest,
        } as UpiNetWithdraw,
      });
      setStep(Steps.FilledForm);
    } else {
      setStep(Steps.TypingData);
    }
  }, [
    currencyId,
    phone,
    amount,
    email,
    bankAccount,
    bankCode,
    bankName,
    userInfo,
    name,
    sureName,
    branchCode,
  ]);

  const openPortal = () => {
    setPortalState(true);
  };

  const chooseBank = (b: any) => (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setBankCode(b.name);
    setBankNameId(b.id);
    setPortalState(false);
  };

  return (
    <>
      <AmountInput
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        placeholder="walletPopUp.forms.amount"
        isWithdraw
        size="full"
        onChange={setAmount}
        isPresets={false}
        min={limit.min}
        max={limit.max}
        wrapperClasses={styles.marginInputBottom}
      />

      {/* Phone */}
      <PhoneInput
        countryCodeDefault="IN"
        withConfirm={false}
        size="full"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        sendKey="09x"
        onChange={setPhone}
        isHidePlaceholder={false}
        baseValue="+91"
        placeholder="walletPopUp.forms.phone"
        classesWrapper={styles.marginInputBottom}
      />

      <InputUserInfo
        userInfoKey="name"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        placeholder="walletPopUp.forms.name"
        size="full"
        classes={styles.marginInputBottom}
        onChange={setName}
        field={name}
      />

      <InputUserInfo
        userInfoKey="surname"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        placeholder="walletPopUp.forms.sureName"
        size="full"
        classes={styles.marginInputBottom}
        onChange={setSurName}
        field={sureName}
      />

      <InputUserInfo
        userInfoKey="email"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        placeholder="walletPopUp.forms.email"
        size="full"
        classes={styles.marginInputBottom}
        onChange={setEmail}
        field={email}
      />

      <Input
        placeholder="walletPopUp.forms.bankCode"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        size="full"
        classes={styles.marginInputBottom}
        errorText={bankCode.errorText}
        onChange={handleChangeInput('bankCode')}
      />
      <Input
        placeholder="walletPopUp.forms.bankAccount"
        inputStyleType="border-colorful"
        inputLabelType="labelUp"
        size="full"
        classes={styles.marginInputBottom}
        errorText={bankAccount.errorText}
        onChange={handleChangeInput('bankAccount')}
      />

      {activeChannelId === PaymentChannelType.NetBanking && (
        <Input
          placeholder="walletPopUp.forms.bankBranchCode"
          inputStyleType="border-colorful"
          inputLabelType="labelUp"
          size="full"
          classes={styles.marginInputBottom}
          errorText={branchCode.errorText}
          onChange={handleChangeInput('branchCode')}
        />
      )}

      <Selector
        placeholder="walletPopUp.forms.bank"
        activeId={bankNameId}
        selectorStyleType="labelUp"
        handleClick={openPortal}
        data={BANK_NAMES}
        classes={styles.marginInputBottom}
        type="button"
        isOpenUp
        size="full"
      />

      <PortalList
        isOpen={isOpenPortal}
        data={BANK_NAMES}
        callback={chooseBank}
        activeId={bankNameId}
      />
      {/* {isOpenPortal && (
        <PortalLayout classes={styles.portalInx}>
          <div className={styles.list}>
            <PopUpHeader title="walletPopUp.forms.bank" />

            <div className={styles.portalContent}>
              {BANK_NAMES.map((b) => (
                <div
                  className={cln(styles.bankInfo, {
                    [styles.active]: bankNameId === b.id,
                  })}
                  key={b.id}
                  onClick={chooseBank(b)}
                >
                  <div>{b.name}</div>
                  {bankNameId === b.id && <SelectedSvg />}
                </div>
              ))}
            </div>
          </div>
        </PortalLayout>
      )} */}
    </>
  );
};

export default UPINet;
